import React, { useState } from 'react';
import Datetime from 'react-datetime';
import { Global } from '@emotion/core';
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  Path,
} from 'react-hook-form';
import { Caret } from 'shared/Caret';
import { useT } from 'client/i18n/hooks/useT';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { useDateTimeFormatter } from 'client/i18n/hooks/useDateTimeFormatter';
import { Modal } from '@spotify-internal/anchor-ds';
import { ButtonPrimary, ButtonSecondary } from '@spotify-internal/encore-web';
import {
  Container,
  Dropdown,
  DropdownWrapper,
  LocalTimezoneMessage,
  PseudoLabel,
  PublishDateDisplay,
  DatetimeGlobalStyles,
} from './styles';
import { roundToNextHour } from './utils';

export const ControlledFieldDatePicker = <T extends FieldValues>({
  label,
  name = 'publishOn' as Path<T>,
  className,
  control,
}: {
  label: string;
  name: FieldPath<T>;
  className?: any;
  control?: Control<T>;
}) => {
  const [isShowingSchedulerModal, setIsShowingSchedulerModal] = useState(false);
  const {
    field: { value, onChange },
  } = useController<T, FieldPath<T>>({ control, name });
  const [valueOnOpen, setValueOnOpen] = useState(value);
  const t = useT();
  const { locale } = useLocale();
  const dateFormatter = useDateTimeFormatter({
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  const timeFormatter = useDateTimeFormatter({
    hour: 'numeric',
    minute: 'numeric',
  });

  const dateTimeValue = !value || value === '' ? Date.now() : value;

  return (
    <Container className={className}>
      <PseudoLabel aria-label={name}>{label}</PseudoLabel>
      <DropdownWrapper>
        <button
          onClick={() => {
            setIsShowingSchedulerModal(true);
            setValueOnOpen(value);
          }}
          type="button"
          aria-label={t(
            'FieldDatePicker.ButtonAriaLabel',
            'Open date selector',
            'Date picker button screenreader information'
          )}
        >
          <Dropdown>
            <PublishDateDisplay>
              {value
                ? t(
                    'FieldDatePicker.ScheduleForButtonLabel',
                    '{date} at {time}',
                    'Epsiode publication scheduled for label',
                    {
                      date: dateFormatter.format(new Date(value)),
                      time: timeFormatter.format(new Date(value)),
                    }
                  )
                : t(
                    'FieldDatePicker.NowLabel',
                    'Now',
                    'Episode publish now label'
                  )}
            </PublishDateDisplay>
            <Caret color="#5f6369" />
          </Dropdown>
        </button>
      </DropdownWrapper>
      <Modal
        size="small"
        aria-labelledby="date-picker-title"
        isShowing={isShowingSchedulerModal}
        onClose={() => {
          onChange(valueOnOpen);
          setIsShowingSchedulerModal(false);
        }}
        isShowingCloseButton
      >
        <Modal.Title id="date-picker-title">
          {value
            ? t(
                'FieldDatePicker.ScheduleFor.Title',
                'Schedule for {date} at {time}',
                'Epsiode publication scheduled for label',
                {
                  date: dateFormatter.format(new Date(value)),
                  time: timeFormatter.format(new Date(value)),
                }
              )
            : t(
                'FieldDatePicker.PublishNowLabel',
                'Publish now',
                'Episode publish now label'
              )}
        </Modal.Title>
        <Modal.Body
          as="div"
          style={{
            width: '100%',
            maxWidth: '300px',
          }}
        >
          <Global styles={DatetimeGlobalStyles} />
          <Datetime
            locale={locale}
            className="DatetimeContainer"
            input={false}
            timeConstraints={{
              minutes: { min: 0, max: 0, step: 60 },
              seconds: { min: 0, max: 0, step: 60 },
              milliseconds: { min: 0, max: 0, step: 1000 },
            }}
            onChange={date => {
              onChange(roundToNextHour(date));
            }}
            value={dateTimeValue as Date}
            aria-labelledby="date-picker-title"
          />
          <LocalTimezoneMessage>
            {t(
              'FieldDatePicker.LocalTimeZonesMessage',
              'All times are local',
              'Date picker local time zones message'
            )}
          </LocalTimezoneMessage>
        </Modal.Body>
        <Modal.Footer>
          <ButtonSecondary
            onClick={() => {
              onChange(null);
              setIsShowingSchedulerModal(false);
            }}
          >
            {t(
              'FieldDatePicker.ResetToNowButton',
              'Reset to now',
              'Date picker reset to now button text'
            )}
          </ButtonSecondary>
          <ButtonPrimary onClick={() => setIsShowingSchedulerModal(false)}>
            {t('Button.Confirm', 'Confirm', 'Confirm button text')}
          </ButtonPrimary>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
